import { css } from '@emotion/react';
import { Box, Flex, Stack, Text, Title, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { Trans, useTranslation } from 'next-i18next';

import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';
import StartInstructorButton from './StartInstructorButton';
import { JOIN_INSTRUCTOR_SPOTS } from './constants/gtmEventParams';
import { useHeroAbExperiment } from './useHeroAbExperiment';

const HERO_COPY_KEYS = {
  original: {
    heading: 'open.knowledge.hero.heading',
    subtitle: 'open.knowledge.hero.subtitle',
    description: 'open.knowledge.hero.description',
  },
  revenue: {
    heading: 'open.knowledge.hero.ab.heading.revenue',
    subtitle: 'open.knowledge.hero.ab.subtitle.revenue',
    description: 'open.knowledge.hero.ab.description.revenue',
  },
  ease: {
    heading: 'open.knowledge.hero.ab.heading.ease',
    subtitle: 'open.knowledge.hero.ab.subtitle.ease',
    description: 'open.knowledge.hero.ab.description.ease',
  },
} as const;

const HeroSection = () => {
  const { t } = useTranslation();
  const { isB, isC, isLoading } = useHeroAbExperiment();

  const variant = isB ? 'revenue' : isC ? 'ease' : 'original';
  const {
    heading: headingKey,
    subtitle: subtitleKey,
    description: descriptionKey,
  } = HERO_COPY_KEYS[variant];

  return (
    <Box component="section" css={styleSection}>
      <Flex direction="column" align="center" gap={48} css={styleContainer}>
        <Stack spacing={16} css={styleTextStack}>
          {isLoading ? (
            <>
              {/* A/B 테스트 중일 때 CSR로 렌더링하기 위해 로딩 동안 임시 스켈레톤 노출 */}
              <Box css={styleSkeletonHeading} aria-hidden />
              <Stack>
                <Box css={styleSkeletonSubtitle} aria-hidden />
                <Box css={styleSkeletonDescription} aria-hidden />
              </Stack>
            </>
          ) : (
            <>
              <Text color="gray.9" css={styleHeading}>
                <Trans
                  i18nKey={headingKey}
                  components={{
                    green: <span css={styleGreenText} />,
                    br: <br />,
                  }}
                />
              </Text>
              <Stack>
                <Title order={3} weight={700} color="dark.9" css={styleSubBold}>
                  {t(subtitleKey)}
                </Title>
                <Box css={styleSubText}>
                  <Text color="gray.9" css={styleSubNormal}>
                    <Trans i18nKey={descriptionKey} components={{ br: <br /> }} />
                  </Text>
                </Box>
              </Stack>
            </>
          )}
        </Stack>
        <StartInstructorButton
          spot={JOIN_INSTRUCTOR_SPOTS.HERO}
          variant="filled"
          color="dark"
          miw={260}
          h={56}
        >
          {t('open.knowledge.cta.start')}
        </StartInstructorButton>
      </Flex>
    </Box>
  );
};

export default HeroSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  paddingTop: 200,
  paddingBottom: 200,
  backgroundImage: `url("${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/title_bg_lg.png")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  [desktopFirstMediaQuery.tablet]: {
    paddingTop: 160,
    paddingBottom: 160,
  },
  [desktopFirstMediaQuery.mobile]: {
    backgroundImage: `url("${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/title_bg_sm.png")`,
    paddingTop: 120,
    paddingBottom: 120,
  },
});

const styleContainer = css({
  position: 'relative',
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleTextStack = css({
  width: '100%',
  textAlign: 'center',
});

const styleSkeletonHeading = css({
  height: 144,
  width: '100%',
});

const styleSkeletonSubtitle = css({
  height: 33,
  width: '100%',
});

const styleSkeletonDescription = css({
  height: 33,
  width: '100%',
});

const styleHeading = css({
  fontSize: 56,
  fontWeight: 700,
  lineHeight: 1.3,
  wordBreak: 'keep-all',
  [desktopFirstMediaQuery.tablet]: {
    fontSize: 44,
  },
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 28,
  },
});

const styleGreenText = css({
  color: '#00a760',
});

const styleSubText = css({
  textAlign: 'center',
  fontSize: 22,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleSubBold = css({
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleSubNormal = css({
  fontWeight: 400,
  fontSize: 22,
  lineHeight: 1.5,
  wordBreak: 'keep-all',
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});
